<template>
  <Empathize
    :animation="empathizeAnimation"
    class="x-z-40 x-rounded-md x-bg-neutral-0 desktop:x-w-full"
    :events-to-close-empathize="isDesktopOrGreater ? desktopCloseEvents : mobileCloseEvents"
  >
    <component
      :is="isDesktopOrGreater ? 'div' : 'BaseScroll'"
      class="x-h-full x-rounded-md desktop:x-ml-16"
      :class="{ 'x-bg-neutral-0': showEmpathize }"
    >
      <div
        v-if="showEmpathize || showIdentifierResults"
        class="x-layout-item x-pb-16 desktop:x-block desktop:x-pl-16 desktop:x-pt-16"
        :class="{ 'x-mb-40 x-border-b x-border-neutral-10': !x.query.search && isTabletOrLess }"
      >
        <BaseKeyboardNavigation
          class="x-flex x-flex-col desktop:x-grid desktop:x-grid-cols-12 desktop:x-items-start desktop:x-gap-24"
          :navigation-hijacker="navigationHijacker"
        >
          <PredictiveIdentifierResults
            v-if="showIdentifierResults"
            :animation="suggestionsAnimation"
          />

          <div
            v-else-if="showEmpathize"
            class="x-flex x-flex-col x-gap-20 x-pl-8 x-pt-16 desktop:x-col-span-5 desktop:x-pl-0 desktop:x-pt-0"
            :class="x.query.searchBox ? 'desktop:x-gap-4' : 'desktop:x-gap-16'"
          >
            <div v-if="showHistoryQueries" class="x-flex x-flex-col x-gap-12">
              <div v-if="!x.query.searchBox" class="x-flex x-items-center">
                <h1 class="x-title3 x-flex-1 x-text-lead-75">
                  {{ $t('historyQueries.title') }}
                </h1>

                <BaseIdModalOpen
                  v-if="isTabletOrLess && !x.query.searchBox"
                  modal-id="my-history-aside"
                  class="x-button-neutral x-button-sm x-button-tight x-title4 x-self-end x-pr-8 x-text-[16px] x-capitalize x-text-lead-75"
                >
                  <SettingsIcon class="x-icon-lg" />
                  {{ $t('myHistory.openButton') }}
                </BaseIdModalOpen>
                <ClearHistoryQueries
                  v-else
                  class="x-button-neutral x-button-sm x-button-tight x-title4 x-title4-sm x-px-8 x-text-lead-75"
                >
                  <TrashIcon v-if="isTabletOrLess" class="x-icon-lg" />
                  <span v-else>{{ $t('historyQueries.clear') }}</span>
                </ClearHistoryQueries>
              </div>

              <SlidingPanel
                v-if="!isDesktopOrGreater"
                :show-buttons="false"
                :reset-on-content-change="false"
                button-class="x-button-lead x-button-circle x-button-ghost x-p-0"
              >
                <PredictiveHistoryQueries />
              </SlidingPanel>
              <PredictiveHistoryQueries v-else />
            </div>

            <PredictiveQuerySuggestions
              v-if="showQuerySuggestions"
              :animation="suggestionsAnimation"
            />

            <PredictiveNextQueries v-if="showNextQueries" :animation="suggestionsAnimation" />

            <PredictivePopularSearches
              v-if="showPopularSearches"
              :animation="suggestionsAnimation"
            />

            <BaseIdModalOpen
              v-if="isDesktopOrGreater && !x.query.searchBox"
              modal-id="my-history-aside"
              class="x-button-neutral x-button-sm x-button-tight x-self-start x-text-neutral-90"
              data-test="my-history-button"
            >
              <SettingsIcon />
              {{ $t('myHistory.openButton') }}
            </BaseIdModalOpen>
          </div>

          <SlidingRecommendations
            v-if="isDesktopOrGreater && !x.query.searchBox"
            class="x-col-span-7"
          />
        </BaseKeyboardNavigation>
      </div>
      <SlidingRecommendations v-if="isTabletOrLess && !x.query.searchBox" />
    </component>
  </Empathize>
</template>

<script setup lang="ts">
import type { XEventsTypes } from '@empathyco/x-components'
import {
  animateScale,
  BaseIdModalOpen,
  BaseKeyboardNavigation,
  SettingsIcon,
  SlidingPanel,
  StaggeredFadeAndSlide,
  TrashIcon,
  use$x,
} from '@empathyco/x-components'
import { Empathize } from '@empathyco/x-components/empathize'
import { ClearHistoryQueries } from '@empathyco/x-components/history-queries'
import { useDevice } from '../../composables/use-device.composable'
import { usePredictiveHelpers } from '../../composables/use-predictive-helpers.composable'
import PredictiveHistoryQueries from './predictive-history-queries.vue'
import PredictiveIdentifierResults from './predictive-identifier-results.vue'
import PredictiveNextQueries from './predictive-next-queries.vue'
import PredictivePopularSearches from './predictive-popular-searches.vue'
import PredictiveQuerySuggestions from './predictive-query-suggestions.vue'
import SlidingRecommendations from './sliding-recommendations.vue'

const empathizeAnimation = animateScale()
const suggestionsAnimation = StaggeredFadeAndSlide as any
const { isDesktopOrGreater, isTabletOrLess } = useDevice()
const x = use$x()

const {
  navigationHijacker,
  showIdentifierResults,
  showHistoryQueries,
  showQuerySuggestions,
  showNextQueries,
  showPopularSearches,
  showEmpathize,
} = usePredictiveHelpers()

const mobileCloseEvents: Array<keyof XEventsTypes> = [
  'UserClosedEmpathize',
  'UserSelectedASuggestion',
  'UserPressedEnterKey',
]
const desktopCloseEvents: Array<keyof XEventsTypes> = [...mobileCloseEvents, 'UserBlurredSearchBox']
</script>
<style lang="scss">
.x-result-link:focus > * {
  outline: -webkit-focus-ring-color auto 1px;
}
</style>
